import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Maximize2, ZoomIn, ZoomOut } from "lucide-react";
import { useDocPages } from "../useDocPages";
import { cls } from "../../ui";

/** Compact in-pane page viewer for the bench; full annotation/signature tooling lives in /documents/:id/view. */
export function DocInline({ doc, onClose }) {
  const nav = useNavigate();
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const get = useDocPages(doc.id, doc.version);
  const total = doc.pages || 1;
  const url = get(page, scale);
  const B = ({ onClick, disabled, title, children, testId }) => <button className="text-slate-600 hover:text-slate-900 disabled:opacity-30 px-1" onClick={onClick} disabled={disabled} title={title} data-testid={testId}>{children}</button>;
  return (
    <div className="ent-panel flex flex-col h-full min-h-0" data-testid="doc-inline">
      <div className="flex items-center gap-1 px-1.5 h-6 border-b border-slate-300 bg-[#f1f4f7] text-[11px] shrink-0">
        <span className="font-semibold truncate" data-testid="doc-inline-title">{doc.title}</span><span className="text-slate-500">· {String(doc.type || "").replace(/_/g, " ")} · v{doc.version}</span>
        <div className="ml-auto flex items-center">
          <B onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} title="Previous page" testId="doc-inline-prev"><ChevronLeft className="w-3.5 h-3.5" /></B>
          <span className="px-1" data-testid="doc-inline-page">Page {page} of {total}</span>
          <B onClick={() => setPage((p) => Math.min(total, p + 1))} disabled={page >= total} title="Next page" testId="doc-inline-next"><ChevronRight className="w-3.5 h-3.5" /></B>
          <B onClick={() => setScale((s) => Math.max(0.6, s - 0.2))} title="Zoom out" testId="doc-inline-zoom-out"><ZoomOut className="w-3.5 h-3.5" /></B>
          <B onClick={() => setScale((s) => Math.min(2.4, s + 0.2))} title="Zoom in" testId="doc-inline-zoom-in"><ZoomIn className="w-3.5 h-3.5" /></B>
          <B onClick={() => nav(`/documents/${doc.id}/view`)} title="Open full document workspace (annotate / sign)" testId="doc-inline-full"><Maximize2 className="w-3.5 h-3.5" /></B>
          <B onClick={onClose} title="Close" testId="doc-inline-close"><X className="w-3.5 h-3.5" /></B>
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-slate-200 p-2 grid place-items-start justify-center">
        {url === "error" ? <div className="text-[11px] text-red-700 p-2" data-testid="doc-inline-error">This document cannot be rendered here — open the full viewer.</div>
          : url ? <img src={url} alt={`Page ${page}`} className={cls("shadow-sm bg-white")} data-testid="doc-inline-img" /> : <div className="text-[11px] text-slate-500 p-2">Rendering page…</div>}
      </div>
    </div>
  );
}
